import { CSSProperties, useMemo } from 'react';
import { Group, UnstyledButton, Tooltip, Stack, Divider, Transition } from '@mantine/core';
import Text from '@ui/components/Text/Text';
import Badge, { BADGE_LIGHT_STYLES } from '@ui/components/Badge/Badge';
import Callout from '@ui/components/Callout/Callout';

import Project from 'src/entities/project';
import SubscriptionPlanSelectionModal
	from 'src/new_arch/modules/workspaces/modals/SubscriptionPlanSelectionModal/SubscriptionPlanSelectionModal';
import PermissionsSwitch from '../PermissionsSwitch/PermissionsSwitch';
import useHideCommentsForReviewers from './useHideCommentsForReviewers';

export type HideCommentsForReviewersSwitchProps = {
	project: Project;
	modalsStackOrder?: number;
};

const HideCommentsForReviewersSwitch = ( { project, modalsStackOrder = 1 }: HideCommentsForReviewersSwitchProps ) => {
	const {
		commentsHiddenForReviewers,
		onHiddenForReviewersChange,
		isSwitchEnabled,
		planWithRequiredFeature,
		isShowingSubscriptionPlanSelectionModal,
		onRequiredPlanBadgeClick,
		showChangeSubscriptionPlanModalOnClick,
		cantUpgradeSubscriptionTootlip,
		onSubscriptionPlanSelectionModalClose
	} = useHideCommentsForReviewers( { project } );

	const planButtonStyles = useMemo( () => ( {
		display: 'flex',
		alignItems: 'center',
		...( showChangeSubscriptionPlanModalOnClick ? { pointerEvents: 'all' } : { pointerEvents: 'none' } )
	} as CSSProperties ), [ showChangeSubscriptionPlanModalOnClick ] );

	return (
		<>
			<Tooltip
				label={cantUpgradeSubscriptionTootlip}
				withinPortal
				disabled={!cantUpgradeSubscriptionTootlip}
				position="right"
				offset={10}
				w={230}
				multiline
			>
				<Stack spacing={8}>
					<PermissionsSwitch
						label={(
							<Group spacing={4}>
								<Text variant="h5">Hide Comments</Text>
								{planWithRequiredFeature && (
									<UnstyledButton
										onClick={onRequiredPlanBadgeClick}
										style={planButtonStyles}
										disabled={!showChangeSubscriptionPlanModalOnClick}
										aria-label={`Upgrade to ${planWithRequiredFeature.name}`}
									>
										<Badge color="green" size="xs" variant="light" styles={BADGE_LIGHT_STYLES}>
											{planWithRequiredFeature.name} Only
										</Badge>
									</UnstyledButton>
								)}
							</Group>
						)}
						aria-label="Hide Comments"
						description="Only comments from the current round will be visible"
						checked={commentsHiddenForReviewers}
						onChange={onHiddenForReviewersChange}
						disabled={!isSwitchEnabled}
						styles={{ root: { borderBottom: 'none' } }}
					/>
					<Transition mounted={commentsHiddenForReviewers} transition="fade" duration={400} timingFunction="ease">
						{( styles: CSSProperties ) => (
							<Callout
								style={styles}
								text={`Now this will hide past round comments, including thumbnail notifications
									and related filters for unresolved comments and @ mentions for Reviewer roles.`}
								type="promotional"
							/>
						)}
					</Transition>
					<Divider color="gray.1" />
				</Stack>
			</Tooltip>

			{isShowingSubscriptionPlanSelectionModal && (
				<SubscriptionPlanSelectionModal
					stackOrder={modalsStackOrder}
					onClose={onSubscriptionPlanSelectionModalClose}
				/>
			)}
		</>
	);
};

export default HideCommentsForReviewersSwitch;
